import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/** A face is sent as a square of at most this many pixels a side; every avatar draws it as a circle. */
const FRAME_SIZE = 512;
/** How far the slider, the wheel and a pinch may zoom in: a face from across a room. */
export const FRAME_MAX_ZOOM = 4;
const KEY_STEP = 10; // screen pixels an arrow key moves the photo

export interface PhotoFraming {
  /** The square window the photo sits under. Focusable, so the arrow keys reach it. */
  stage: RefObject<HTMLDivElement | null>;
  /** The photo itself, drawn with `photo.url` once it has opened. */
  image: RefObject<HTMLImageElement | null>;
  /** A range input from 1 to {@link FRAME_MAX_ZOOM}, kept in step with the zoom. */
  slider: RefObject<HTMLInputElement | null>;
  /** The opened photo, null until it has opened. */
  photo: { bitmap: ImageBitmap; url: string } | null;
  /** True when the browser could not draw the file at all. */
  failed: boolean;
  /** The square inside the window, as a JPEG ready for `POST /api/uploads/avatar`. */
  crop: () => Promise<File>;
}

/**
 * The framing behind «Фото профиля»: the photo sits under a circular window it always
 * fills, and whoever is putting their face up drags and zooms it until the part they
 * want is in the circle — drag (or the arrow keys) to move it; the slider, the wheel,
 * a pinch or +/− to zoom. The same as `frameAvatar` in the phone apps.
 *
 * Only the behaviour lives here. Each surface draws the window, the slider and the
 * buttons in its own dialog, and hands the three elements back through the refs.
 */
export function usePhotoFrame(file: File): PhotoFraming {
  const stage = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const slider = useRef<HTMLInputElement>(null);
  const cropRef = useRef<(() => Promise<File>) | null>(null);
  const [photo, setPhoto] = useState<{ bitmap: ImageBitmap; url: string } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    let opened: { bitmap: ImageBitmap; url: string } | undefined;
    setPhoto(null);
    setFailed(false);
    createImageBitmap(file, { imageOrientation: 'from-image' }).then(
      (bitmap) => {
        opened = { bitmap, url: URL.createObjectURL(file) };
        if (live) setPhoto(opened);
        else release(opened);
      },
      () => live && setFailed(true),
    );
    return () => {
      live = false;
      if (opened) release(opened);
    };
  }, [file]);

  useEffect(() => {
    const box = stage.current;
    const img = image.current;
    const range = slider.current;
    if (!photo || !box || !img || !range) return;
    const { bitmap } = photo;
    const { width: w, height: h } = bitmap;

    // The view is kept in image pixels: the point under the window's centre, and a
    // zoom where 1 just fills it.
    let cx = w / 2;
    let cy = h / 2;
    let zoom = 1;

    const side = () => box.clientWidth;
    const scale = () => (side() / Math.min(w, h)) * zoom; // screen pixels per image pixel

    function draw() {
      const s = scale();
      // Keep the photo covering the whole window.
      const half = side() / 2 / s;
      cx = Math.min(Math.max(cx, half), w - half);
      cy = Math.min(Math.max(cy, half), h - half);
      img!.style.transform = `translate(${side() / 2 - cx * s}px, ${side() / 2 - cy * s}px) scale(${s})`;
      range!.value = String(zoom);
    }

    /** Zoom to `z`, keeping the image point under (`px`, `py`) in the window where it is. */
    function zoomAt(z: number, px = side() / 2, py = side() / 2) {
      const before = scale();
      zoom = Math.min(Math.max(z, 1), FRAME_MAX_ZOOM);
      const after = scale();
      cx += (px - side() / 2) * (1 / before - 1 / after);
      cy += (py - side() / 2) * (1 / before - 1 / after);
      draw();
    }

    function moveBy(dx: number, dy: number) {
      const s = scale();
      cx -= dx / s;
      cy -= dy / s;
      draw();
    }

    // Pointers down on the stage: one drags, two pinch.
    const pointers = new Map<number, { x: number; y: number }>();
    const local = (event: PointerEvent | WheelEvent) => {
      const r = box.getBoundingClientRect();
      return { x: event.clientX - r.left, y: event.clientY - r.top };
    };
    const spread = () => {
      const [a, b] = [...pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
      return { d: Math.hypot(a.x - b.x, a.y - b.y), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };
    const down = (event: PointerEvent) => {
      box.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, local(event));
      box.focus({ preventScroll: true });
    };
    const move = (event: PointerEvent) => {
      const prev = pointers.get(event.pointerId);
      if (!prev) return;
      const p = local(event);
      if (pointers.size === 2) {
        const before = spread();
        pointers.set(event.pointerId, p);
        const now = spread();
        moveBy(now.x - before.x, now.y - before.y);
        if (before.d) zoomAt(zoom * (now.d / before.d), now.x, now.y);
      } else {
        pointers.set(event.pointerId, p);
        moveBy(p.x - prev.x, p.y - prev.y);
      }
    };
    const up = (event: PointerEvent) => pointers.delete(event.pointerId);
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      const p = local(event);
      zoomAt(zoom * Math.exp(-event.deltaY * 0.002), p.x, p.y);
    };
    const key = (event: KeyboardEvent) => {
      const moves: Record<string, [number, number]> = {
        ArrowLeft: [KEY_STEP, 0],
        ArrowRight: [-KEY_STEP, 0],
        ArrowUp: [0, KEY_STEP],
        ArrowDown: [0, -KEY_STEP],
      };
      const step = moves[event.key];
      if (step) moveBy(...step);
      else if (event.key === '+' || event.key === '=') zoomAt(zoom * 1.1);
      else if (event.key === '-') zoomAt(zoom / 1.1);
      else return;
      event.preventDefault();
    };
    const slide = () => zoomAt(Number(range.value));

    box.addEventListener('pointerdown', down);
    box.addEventListener('pointermove', move);
    box.addEventListener('pointerup', up);
    box.addEventListener('pointercancel', up);
    box.addEventListener('wheel', wheel, { passive: false });
    box.addEventListener('keydown', key);
    range.addEventListener('input', slide);
    // The window's size comes from the layout, so place the photo once it has one,
    // and again if it changes.
    const resize = new ResizeObserver(draw);
    resize.observe(box);

    /** The square inside the window, scaled down to at most FRAME_SIZE pixels a side. */
    cropRef.current = async () => {
      const span = side() / scale();
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = Math.max(1, Math.min(Math.round(span), FRAME_SIZE));
      canvas
        .getContext('2d')
        ?.drawImage(bitmap, cx - span / 2, cy - span / 2, span, span, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise<Blob | null>((done) => canvas.toBlob(done, 'image/jpeg', 0.85));
      if (!blob) throw new Error('Не удалось подготовить фото');
      return new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
    };

    return () => {
      cropRef.current = null;
      resize.disconnect();
      box.removeEventListener('pointerdown', down);
      box.removeEventListener('pointermove', move);
      box.removeEventListener('pointerup', up);
      box.removeEventListener('pointercancel', up);
      box.removeEventListener('wheel', wheel);
      box.removeEventListener('keydown', key);
      range.removeEventListener('input', slide);
    };
  }, [photo]);

  const crop = () => {
    const cut = cropRef.current;
    return cut ? cut() : Promise.reject(new Error('Фото ещё не открылось'));
  };

  return { stage, image, slider, photo, failed, crop };
}

function release({ bitmap, url }: { bitmap: ImageBitmap; url: string }) {
  bitmap.close();
  URL.revokeObjectURL(url);
}
